import React, { useState, useEffect } from "react";
import CommonTitle from "../common/DeliveryCommonTitle";
import {
  verifyResidentAPI,
  fetchResidentsByFlat,
} from "../../services/delivery_api"; // Add fetchResidentsByFlat
import { useDeliverySession } from "../../hooks/useDeliverySession";
import CommonPopup from "../common/DeliveryCommonPopup";
import LoadingSpinner from "../common/LoadingSpinner";
import { useDeliveryAuthPolling } from "../../hooks/useDeliveryAuthPolling";
import { useTranslation } from "react-i18next";

const ResidentDetailsScreen = ({ onNext, onBack, setStep }) => {
  const { t } = useTranslation();

  const [name, setName] = useState("");
  const [wing, setWing] = useState("");
  const [flatNo, setFlatNo] = useState("");
  const [parcelCount, setParcelCount] = useState(0);
  const [errors, setErrors] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [otpRequired, setOtpRequired] = useState(false);
  const [showMaxWarning, setShowMaxWarning] = useState(false);
  const [residentOptions, setResidentOptions] = useState([]);
  const [isLoadingResidents, setIsLoadingResidents] = useState(false);
  const [nameError, setNameError] = useState(false);
  const { session, setSession } = useDeliverySession();
  const [selectedResidentId, setSelectedResidentId] = useState("");
  const [selectedResidentType, setSelectedResidentType] = useState("");

  useDeliveryAuthPolling(session, setSession, 10000);

  const isFormValid =
    name.trim() && wing.trim() && flatNo.trim() && parcelCount > 0;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Clear parcel count if returning from a later step
  useEffect(() => {
    const returnToStep = localStorage.getItem("returnToStep");
    if (returnToStep === "6" || returnToStep === "5") {
      setParcelCount(0);
    }
  }, []);

  // Fetch residents when flat number changes (debounced)
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (wing.trim() && flatNo.trim() && session.building) {
        fetchResidents();
      } else {
        setResidentOptions([]);
        setName(""); // Clear name when flatNo is cleared
        setSelectedResidentId("");
        setSelectedResidentType("");
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [wing, flatNo, session.building]);

  // Add this useEffect to sync selected resident when name changes
  useEffect(() => {
    if (name && residentOptions.length > 0) {
      const currentResident = residentOptions.find(
        (r) => r.resident_name === name
      );
      if (currentResident) {
        console.log("Syncing resident from options:", currentResident);
        setSelectedResidentId(currentResident.resident_id);
        setSelectedResidentType(currentResident.type);
      }
    }
  }, [name, residentOptions]);

  const maskName = (name = "") => {
    if (!name) return "";
    return name
      .split(" ")
      .map((part) => {
        if (part.length <= 1) return "*";
        if (part.length === 2) return `${part[0]}*`;
        if (part.length === 3) return `${part[0]}*${part[2]}`;
        return `${part.slice(0, 2)}***${part.slice(-1)}`;
      })
      .join(" ");
  };

  const fetchResidents = async () => {
    setIsLoadingResidents(true);
    try {
      const payload = {
        web_input_resident_person_wing_name: wing,
        web_input_resident_person_flat_no: parseInt(flatNo),
        web_input_building_id: session.building || null,
      };

      const result = await fetchResidentsByFlat(payload);

      if (
        result?.success &&
        result?.data &&
        Array.isArray(result.data.people)
      ) {
        const people = result.data.people;

        // Map to a consistent format for the dropdown
        const mappedResidents = people.map((person) => ({
          resident_id: person.id || person.resident_id, // Use person.id OR person.resident_id
          resident_name: person.name,
          // phone_number: person.phone_number || "",
          // wing_name: result.data.wing_name,
          // flat_no: result.data.flat_no.toString(),
          type: person.type || "resident", // Default to "resident" if not specified
          // Optional: keep resident_id if it's a member
          main_resident_id: person.resident_id || person.id,
        }));

        setResidentOptions(mappedResidents);
        // Clear selected resident info when fetching new residents
        setSelectedResidentId("");
        setSelectedResidentType("");

        // Auto-select logic
        if (mappedResidents.length === 1) {
          const single = mappedResidents[0];
          setName(single.resident_name);
          setSelectedResidentId(single.resident_id);
          setSelectedResidentType(single.type);
          // setWing(single.wing_name);
          // setFlatNo(single.flat_no);
          setNameError(false);
          setErrors((prev) => ({
            ...prev,
            name: false,
            // wing: false,
            // flatNo: false,
          }));

          console.log(
            "Auto-selected resident:",
            single.resident_id, // Use single.resident_id, NOT selectedResidentId
            single.type
          );
        } else if (mappedResidents.length > 1) {
          setName(""); // Let user choose
          setSelectedResidentId("");
          setSelectedResidentType("");
        }
      } else {
        setResidentOptions([]);
        setName("");
        setSelectedResidentId("");
        setSelectedResidentType("");
      }
    } catch (error) {
      console.error("Failed to fetch residents:", error);
      setResidentOptions([]);
      setName("");
      setSelectedResidentId("");
      setSelectedResidentType("");
    } finally {
      setIsLoadingResidents(false);
    }
  };

  const validate = () => {
    const err = {};
    if (!name.trim()) {
      err.name = true;
      setNameError(true);
    } else {
      setNameError(false);
    }
    if (!wing.trim()) err.wing = true;
    if (!flatNo.trim()) err.flatNo = true;
    if (!parcelCount || parcelCount < 1 || parcelCount > 10) {
      err.parcelCount = true;
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleProceed = async () => {
    if (!validate()) return;

    // Check if we have a resident ID and type
    if (!selectedResidentId || !selectedResidentType) {
      // Try to find the resident from options
      const currentResident = residentOptions.find(
        (r) => r.resident_name === name
      );
      if (!currentResident) {
        setPopupMessage(
          t(
            "pleaseSelectValidResident",
            "Please select a valid resident from the dropdown"
          )
        );
        setShowPopup(true);
        return;
      }
      // Use the found resident's data
      setSelectedResidentId(currentResident.resident_id);
      setSelectedResidentType(currentResident.type);
    }

    console.log(
      "Proceeding with resident ID:",
      selectedResidentId,
      "type:",
      selectedResidentType
    );

    setIsLoading(true);

    const session_file_name = `dp_${session.da_name}_${session.da_id}_${session.file_ident_code}.json`;

    const payload = {
      // web_input_resident_person_name: name,
      web_input_resident_person_wing_name: wing,
      web_input_resident_person_flat_no: flatNo,
      web_input_total_no_of_resident_parcel: parcelCount,
      web_input_total_no_of_delivery: session.total_no_of_delivery || 1,
      web_input_company_nm: session.company_nm,
      web_input_da_id: session.da_id,
      web_input_file_ident_code: session.file_ident_code,
      web_input_da_name: session.da_name,
      web_input_building_id: session.building || null,
      web_input_resident_req_otp: otpRequired ? "required" : "not_required",
      web_selected_person_id: selectedResidentId,
      web_selected_person_type: selectedResidentType,
    };

    try {
      const response = await verifyResidentAPI(payload);
      const resData = response?.resident_data;

      if (resData?.resident_id) {
        const deliveryObj = {
          resident_id: resData.resident_id,
          resident_token: resData.resident_token || "",
          resident_name: resData.resident_name || name,
          resident_phone_number: resData.resident_phone_number || "",
          wing_name: resData.wing_name || "",
          flat_no: resData.flat_no || flatNo,
          wing: resData.wing || wing,
          building: resData.building || "",
          complex_location: resData.complex_location || "",
          complex_name: resData.complex_name || "",
          pincode: resData.pincode || "",
          approval_sent_time: resData.approval_sent_time || "",
          approval_expiration_time: resData.approval_expiration_time || "",
          is_authenticated: resData.is_authenticated || false,
          is_member: resData.is_member?.toString() || "false",
          number_of_parcel: parcelCount.toString(),
          resident_req_otp: otpRequired ? "required" : "not_required",
          resident_otp_value: "None",
          locker_size: "None",
          locker_open: "False",
          stored_parcel: "False",
          authentication_count: 0,
          circular_loader: "stop",
          reauthentication_timer: "None",
          locker_no: "None",
          session_file_name: session_file_name,
          resident_approval_link: response.approval_url || "",
          captured_images: { links: [] },
          resident_qrcode_shortlink: "",
          resident_locker_open_otp: "None",
        };

        const currentDeliveries = Array.isArray(session.deliveries)
          ? session.deliveries
          : [];

        const updatedDeliveries = [...currentDeliveries, deliveryObj];

        const newDeliveriesCount = updatedDeliveries.length;
        const expectedTotal = session.total_no_of_delivery || 1;
        const shouldUpdateTotal = newDeliveriesCount > expectedTotal;

        console.log(
          "Updating session deliveries Total:",
          shouldUpdateTotal,
          newDeliveriesCount
        );

        setSession((prev) => ({
          ...prev,
          deliveries: updatedDeliveries,
          ...(shouldUpdateTotal && {
            total_no_of_delivery: newDeliveriesCount,
          }),
        }));

        setTimeout(() => {
          onNext({
            resident_id: resData.resident_token,
            parcel_count: parcelCount,
          });
        }, 200);
      } else {
        setPopupMessage(t("residentNotFound", "Resident not found."));
        setShowPopup(true);
      }
    } catch (err) {
      console.error("Resident verification error:", err);
      const msg =
        err.response?.data?.message ||
        t("verificationFailed", "Verification failed. Please try again.");
      setPopupMessage(msg);
      setShowPopup(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    const returnToStep = localStorage.getItem("returnToStep");
    console.log("Return to step on back:", returnToStep);
    if (returnToStep === "5") {
      setStep(5);
    } else {
      onBack();
    }
  };

  // Handler for name selection from dropdown
  const handleNameSelect = (selectedResident) => {
    if (selectedResident) {
      setName(selectedResident.resident_name || "");
      setSelectedResidentId(selectedResident.resident_id || "");
      setSelectedResidentType(selectedResident.type || "");
      // setWing(selectedResident.wing_name || wing);
      // setFlatNo(selectedResident.flat_no || flatNo);
      setNameError(false);
      setErrors((prev) => ({
        ...prev,
        name: false,
        // wing: false,
        // flatNo: false,
      }));
    } else {
      // Clear selection
      setName("");
      setSelectedResidentId("");
      setSelectedResidentType("");
    }
  };

  return (
    <div className="w-full min-h-screen px-4 sm:px-6 py-6 relative">
      {isLoading && (
        <LoadingSpinner
          message={t("verifyingResident", "Verifying resident details...")}
        />
      )}

      <CommonTitle
        title={t("residentDetails", "Resident Details")}
        showBack={true}
        onBack={handleBack}
      />

      <div className="flex items-start justify-center pt-6 sm:pt-10">
        <div className="bg-white rounded-2xl shadow-lg w-full max-w-sm sm:max-w-md px-5 sm:px-8 py-6 sm:py-8 space-y-4 sm:space-y-5">
          {/* Wing and Flat No on same row */}
          <div className="flex gap-3">
            {/* Wing */}
            <div className="flex-1">
              <input
                type="text"
                placeholder={t("wing", "Wing")}
                maxLength={1}
                value={wing}
                onChange={(e) => {
                  const val = e.target.value.toUpperCase();
                  if (/^[A-Z]?$/.test(val)) {
                    setWing(val);
                    setErrors((p) => ({ ...p, wing: false }));
                    // Clear name when wing changes
                    setName("");
                    setResidentOptions([]);
                    setSelectedResidentId("");
                    setSelectedResidentType("");
                  }
                }}
                className={`w-full rounded-xl text-center px-4 py-3 sm:py-3.5 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.wing ? "ring-2 ring-red-500" : "ring-1 ring-gray-300"
                }`}
              />
            </div>

            {/* Flat No */}
            <div className="flex-1">
              <input
                type="text"
                placeholder={t("flatNo", "Flat No")}
                maxLength={5}
                inputMode="numeric"
                value={flatNo}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  setFlatNo(value);
                  setErrors((p) => ({ ...p, flatNo: false }));
                  // Clear name when flat number changes
                  setName("");
                  setResidentOptions([]);
                  setSelectedResidentId("");
                  setSelectedResidentType("");
                }}
                className={`w-full rounded-xl text-center px-4 py-3 sm:py-3.5 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.flatNo ? "ring-2 ring-red-500" : "ring-1 ring-gray-300"
                }`}
              />
            </div>
          </div>

          {/* Name Dropdown */}
          <div className="relative">
            <select
              value={name}
              onChange={(e) => {
                const selectedName = e.target.value;
                if (selectedName === "") {
                  handleNameSelect(null);
                  return;
                }
                const selected = residentOptions.find(
                  (r) => r.resident_name === selectedName
                );
                if (selected) {
                  handleNameSelect(selected);
                } else {
                  console.warn("Resident not found in options");
                  // Create minimal resident object
                  handleNameSelect({
                    resident_name: selectedName,
                    resident_id: "", // Unknown ID
                    type: "resident", // Default type
                  });
                }
              }}
              disabled={isLoadingResidents || !wing.trim() || !flatNo.trim()}
              className={`w-full rounded-xl text-center px-5 py-3 sm:py-3.5 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none ${
                nameError ? "ring-2 ring-red-500" : "ring-1 ring-gray-300"
              } ${
                isLoadingResidents || !wing.trim() || !flatNo.trim()
                  ? "bg-gray-100 cursor-not-allowed"
                  : "bg-white"
              }`}
            >
              <option value="" disabled>
                {isLoadingResidents
                  ? t("loadingResidents", "Loading residents...")
                  : residentOptions.length === 0
                  ? t("noResidentsFound", "No residents found")
                  : t("selectResident", "Select Resident *")}
              </option>
              {residentOptions.length > 0 &&
                residentOptions.map((resident) => (
                  <option
                    key={resident.resident_id}
                    value={resident.resident_name} // keep ORIGINAL value
                  >
                    {maskName(resident.resident_name)} {/* masked display */}
                  </option>
                ))}
            </select>

            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <svg
                className={`h-5 w-5 ${
                  isLoadingResidents || !wing.trim() || !flatNo.trim()
                    ? "text-gray-400"
                    : "text-gray-500"
                }`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>

            {nameError && (
              <p className="text-red-500 text-sm mt-1 text-center">
                {t("name")} {t("isRequired", "is required")}
              </p>
            )}
          </div>

          {/* OTP Toggle */}
          <div className="text-center space-y-2">
            <p className="font-medium text-gray-700 text-sm sm:text-base">
              {t("otpSharedByCompany", "OTP Shared by Company")}
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setOtpRequired(true)}
                className={`min-w-[110px] sm:min-w-[130px] px-4 py-2.5 rounded-xl border text-sm font-medium transition ${
                  otpRequired
                    ? "bg-blue-100 border-blue-500 text-gray-700"
                    : "bg-white border-gray-300 text-gray-700"
                }`}
              >
                {t("otpRequired", "Required")}
              </button>
              <button
                onClick={() => setOtpRequired(false)}
                className={`min-w-[110px] sm:min-w-[130px] px-4 py-2.5 rounded-xl border text-sm font-medium transition ${
                  !otpRequired
                    ? "bg-blue-100 border-blue-500 text-gray-700"
                    : "bg-white border-gray-300 text-gray-700"
                }`}
              >
                {t("otpNotRequired", "Not Required")}
              </button>
            </div>
          </div>

          {/* Parcel Count */}
          <div className="text-center space-y-3">
            <p className="font-medium text-gray-700 text-sm sm:text-base">
              {t("enterNumberOfParcels", "Enter Number of Parcels")}
            </p>
            <div className="flex justify-center gap-3 flex-wrap">
              {[1, 2, 3, 4].map((num) => (
                <button
                  key={num}
                  onClick={() => setParcelCount(num)}
                  className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl border font-semibold transition ${
                    parcelCount === num
                      ? "bg-blue-100 border-blue-500 text-blue-700"
                      : "bg-white border-gray-300 text-gray-600"
                  }`}
                >
                  {num}
                </button>
              ))}
              <input
                type="number"
                min={1}
                max={10}
                placeholder={t("typeNo", "Type no.")}
                value={parcelCount || ""}
                onChange={(e) => {
                  const value = e.target.value;

                  if (value === "") {
                    setParcelCount(0);
                    setShowMaxWarning(false);
                    return;
                  }

                  const num = parseInt(value, 10);

                  if (!isNaN(num) && num >= 1 && num <= 10) {
                    setParcelCount(num);
                    setShowMaxWarning(false);
                  }

                  if (num > 10) {
                    setShowMaxWarning(true);
                  }
                }}
                onBlur={() => {
                  setShowMaxWarning(false);
                  if (parcelCount > 10) {
                    setParcelCount(10);
                  } else if (parcelCount < 1 && parcelCount !== 0) {
                    setParcelCount(1);
                  }
                }}
                className="w-24 h-11 sm:h-12 rounded-xl text-center border border-gray-300 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              {showMaxWarning && (
                <p className="text-red-500 text-sm mt-2 animate-pulse">
                  {t(
                    "maxparcelsallowed",
                    "Maximum 10 parcels allowed per delivery."
                  )}
                </p>
              )}
            </div>
          </div>

          {/* Proceed Button */}
          <button
            onClick={handleProceed}
            disabled={!isFormValid || isLoading || isLoadingResidents}
            className={`w-full font-bold text-base sm:text-lg py-3.5 sm:py-4 rounded-xl transition ${
              isFormValid && !isLoading && !isLoadingResidents
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {isLoading
              ? t("processing", "PROCESSING...")
              : isLoadingResidents
              ? t("loadingResidents", "LOADING RESIDENTS...")
              : t("proceed", "PROCEED")}
          </button>
        </div>
      </div>

      {showPopup && (
        <CommonPopup
          isOpen={showPopup}
          title={t("error", "Error")}
          message={popupMessage}
          onClose={() => setShowPopup(false)}
        />
      )}
    </div>
  );
};

export default ResidentDetailsScreen;
